import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import * as R from "ramda";

import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { styled } from "@mui/material/styles";

import DialogHeader from "~/components/Modal/ShowMaterialModal/DialogHeader";
// import { trackMaterialClick } from '~/libs/matomo';
import { absolutePageToRelative, relativePageToAbsolute } from "~/utils";

import { getImagePath, getThumbnailPath } from "~/libs/image";

const imagecachehash = window.getClientConfiguration("", ["book", "imagecachehash"]);

const imagecachehashQuery = imagecachehash ? `?cachehash=${imagecachehash}` : "";

const StyledRadio = styled(Radio)(({ theme }) => {
  return {
    "&.MuiRadio-root": {
      "&.Mui-focusVisible": {
        outline: theme.outline(),
        outlineOffset: "-2px"
      }
    }
  };
});

const StyledButton = styled(Button)(({ theme }) => ({
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

class PrintOptionsModal extends Component {
  constructor(props) {
    super(props);

    this.onPageSelect = this.onPageSelect.bind(this);
    this.onPrint = this.onPrint.bind(this);
    this.onAbort = this.onAbort.bind(this);
    this.renderUserMessage = this.renderUserMessage.bind(this);
  }

  componentDidMount() {
    if (this.props.activePages.length === 1 && this.isPrintablePage(this.props.activePages[0])) {
      this.preparePrint();
    }

    const currentPages = R.sort(R.subtract, this.props.activePages);
    for (const page of currentPages) {
      if (this.isPrintablePage(page)) {
        this.props.setPage(page);
        break;
      }
    }
  }

  preparePrint = () => {
    this.props.hideModal();
    this.printPage();
  };

  printPage = () => {
    const { getPage, title, size } = this.props;
    const absolutePage = relativePageToAbsolute(getPage);
    window.issueGlobalPrint(
      [new URL(getImagePath(absolutePage, 2.0), window.location.href).href],
      "main",
      absolutePage,
      `${title} - S.${getPage}`,
      size
    );
  };

  isPrintablePage = page => {
    const { printablePages, showPrintButton: isPrintingEnabled } = this.props;
    const isPrintable = R.includes(page, printablePages);

    return isPrintingEnabled && (isPrintable || R.isEmpty(printablePages));
  };

  onPrintPreviewClick = page => () => {
    const { setPage } = this.props;

    if (this.isPrintablePage(page)) {
      setPage(page);
    }
  };

  onPageSelect(event) {
    this.props.setPage(event.target.value);
  }

  onPrint() {
    this.preparePrint();
  }

  onAbort() {
    this.props.hideModal();
  }

  renderUserMessage() {
    const { activePages, useDefault, additionalInfo } = this.props;
    const isMultiplePages = activePages.length > 1;
    const noun = isMultiplePages ? "Seiten" : "Seite";
    let message = additionalInfo;

    if (useDefault) {
      message = `Drucken dieser ${noun} nicht möglich`;
    }

    return message;
  }

  getImageStyle(page) {
    return {
      cursor: this.isPrintablePage(page) ? "pointer" : "default",
      width: 140,
      border:
        page === relativePageToAbsolute(this.props.getPage) ? "2px solid rgb(66, 155, 244)" : "2px solid lightgrey",
      filter: this.isPrintablePage(page) ? "none" : "grayscale(100%) blur(1px)",
      userSelect: "none"
    };
  }

  render() {
    const { activePages, isAbleToPrint, kind } = this.props;

    const currentPages = R.sort(R.subtract, activePages);

    // trackMaterialClick({
    //   page: getCurrentPageNumber(),
    //   title: material.title,
    // });

    return (
      <>
        <DialogHeader id={`${kind}-modal-title`} title="Drucken" isCloseableOnly />
        <DialogContent>
          {!isAbleToPrint ? (
            <DialogContentText>{this.renderUserMessage()}</DialogContentText>
          ) : (
            <FormControl variant="standard">
              <RadioGroup
                row
                value={relativePageToAbsolute(this.props.getPage)}
                onChange={this.onPageSelect}
                style={{ flexDirection: "row", flexWrap: "nowrap" }}
              >
                {currentPages.map(page => (
                  <div key={`print_preview_${page}`} style={{ margin: "12px 36px 0" }}>
                    <img
                      onClick={this.onPrintPreviewClick(page)}
                      style={this.getImageStyle(page)}
                      src={getThumbnailPath(page)}
                      alt="Thumbnail für Seite"
                      draggable={false}
                    />
                    <FormControlLabel
                      key={page}
                      value={page}
                      control={<StyledRadio color="primary" />}
                      disabled={!this.isPrintablePage(page)}
                      label={`Seite ${absolutePageToRelative(page)}`}
                      labelPlacement="end"
                    />
                  </div>
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        {isAbleToPrint && (
          <DialogActions>
            <StyledButton color="primary" variant="text" onClick={this.onPrint}>
              Drucken
            </StyledButton>
            <StyledButton color="primary" variant="text" onClick={this.onAbort}>
              Abbrechen
            </StyledButton>
          </DialogActions>
        )}
      </>
    );
  }
}

PrintOptionsModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  getPage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activePages: PropTypes.arrayOf(PropTypes.number).isRequired,
  printablePages: PropTypes.arrayOf(PropTypes.number).isRequired,
  isAbleToPrint: PropTypes.bool.isRequired,
  setPage: PropTypes.func.isRequired,
  showPrintButton: PropTypes.bool.isRequired,
  useDefault: PropTypes.bool.isRequired,
  additionalInfo: PropTypes.string,
  kind: PropTypes.string.isRequired
};

export default PrintOptionsModal;
